// ── Direction resolver ──────────────────────────────────────
// Single source of truth for deciding a job's direction (inbound /
// outbound / direct) from the RAW signals the parser extracted. The AI no
// longer asserts direction for Ferrari forms — it reports what it sees and
// this code decides, in MY hub's perspective.
//
// Mental model (hub-relative, NOT leg-relative):
//   outbound = goods are LEAVING the Carolinas (my Carolina endpoint is the origin)
//   inbound  = goods are ARRIVING in the Carolinas (my Carolina endpoint is the destination)
//   direct   = point-to-point move I cover entirely, no airport/hub involved
//   roundtrip = one run doing BOTH: collect inbound pieces at the airport,
//              deliver + pick up along the route, return outbound pieces to
//              the airport. Never auto-detected — only manual or stated.
// I might physically pick up an OUTBOUND parcel, or physically deliver an
// INBOUND one — do not equate pickup=outbound or delivery=inbound.

// States my service area covers. Default Carolinas; overridable per company
// (companyDoc.serviceStates) for the eventual multi-hub world.
export const DEFAULT_SERVICE_STATES = ["NC", "SC"];

// Normalize a free-text direction word into our enum, or null if it isn't one.
export function normalizeDirectionWord(word) {
  const w = String(word || "").trim().toLowerCase();
  if (!w) return null;
  if (w.startsWith("inbound") || w === "in") return "inbound";
  if (w.startsWith("outbound") || w === "out") return "outbound";
  if (w.startsWith("direct")) return "direct";
  // Combined run: airport → deliveries + pickups → airport, one job number.
  if (w.startsWith("round") || w === "rt") return "roundtrip";
  return null;
}

/**
 * Decide direction from extracted raw signals. Ordered cascade — stop at the
 * first match. Returns { jobType, source, needsReview }.
 *
 *   extracted  — the parser's raw-signal object (see parseEmail prompt):
 *                { documentShape, destAirport, statedDirection,
 *                  shipper{state}, consignee{state}, pickupInfo{state}, ... }
 *   companyDoc — { hubAirport, serviceStates? }
 *   opts       — { manualJobType, multiLeg, statedDirection, aiSuggestedJobType }
 *                soft escape hatches the callers wire in:
 *                  manualJobType      — owner toggled direction by hand (wins)
 *                  multiLeg           — caller already knows it's a schedule
 *                  statedDirection    — broker said a direction word
 *                  aiSuggestedJobType — the model's old jobType guess, used
 *                                       only as a last resort before fallback
 *                                       so non-Ferrari emails don't regress.
 */
export function resolveJobType(extracted = {}, companyDoc = {}, opts = {}) {
  const ex = extracted || {};
  const hub = String(companyDoc.hubAirport || "CLT").toUpperCase();
  const serviceStates = new Set(
    (companyDoc.serviceStates || DEFAULT_SERVICE_STATES).map((s) => String(s).toUpperCase())
  );
  const shape = ex.documentShape || opts.documentShape || null;

  // 1. Manual override always wins.
  const manual = normalizeDirectionWord(opts.manualJobType);
  if (manual) return { jobType: manual, source: "manual", needsReview: false };

  // 2. Multi-leg schedule → direct.
  if (shape === "broker_multi_leg_schedule" || opts.multiLeg) {
    return { jobType: "direct", source: "multi_leg", needsReview: false };
  }

  // 3. Broker stated a direction word (Orion "OUTBOUND", Malca Direction col).
  const stated = normalizeDirectionWord(opts.statedDirection ?? ex.statedDirection);
  if (stated) return { jobType: stated, source: "stated", needsReview: false };

  // 4. Ferrari export letter → always outbound.
  if (shape === "ferrari_export_letter") {
    return { jobType: "outbound", source: "ferrari_export_letter", needsReview: false };
  }

  // 5. Ferrari waybill with a DEST airport box → matches hub = inbound, else outbound.
  if (shape === "ferrari_waybill" && ex.destAirport) {
    const dest = String(ex.destAirport).toUpperCase();
    return {
      jobType: dest === hub ? "inbound" : "outbound",
      source: "ferrari_dest_airport",
      needsReview: false,
    };
  }

  // 6. No airport box → which service-area endpoint is origin vs destination.
  const consigneeState = String(ex.consignee?.state || "").toUpperCase();
  // Origin can be expressed via shipper OR (more locally) the pickup block.
  const originState = String(ex.shipper?.state || ex.pickupInfo?.state || "").toUpperCase();
  const consigneeLocal = serviceStates.has(consigneeState);
  const originLocal = serviceStates.has(originState);
  if (consigneeLocal && !originLocal) {
    return { jobType: "inbound", source: "endpoint_role", needsReview: false };
  }
  if (originLocal && !consigneeLocal) {
    return { jobType: "outbound", source: "endpoint_role", needsReview: false };
  }

  // 6.9. Non-Ferrari soft fallback: trust the model's old guess if it had one,
  // so plain broker emails (Malca/Orion notifications) don't regress to a
  // blind "outbound". Not authoritative — only reached when nothing above fired.
  const aiGuess = normalizeDirectionWord(opts.aiSuggestedJobType);
  if (aiGuess) return { jobType: aiGuess, source: "ai_guess", needsReview: false };

  // 7. True fallback — flag for manual review.
  return { jobType: "outbound", source: "fallback", needsReview: true };
}

/**
 * Combine a Ferrari address block's parts into a single address string.
 */
function blockAddress(block = {}) {
  const cityState = [block.city, block.state].filter(Boolean).join(", ");
  return [block.address, cityState].filter(Boolean).join(", ").trim();
}

/**
 * Build MY leg's local stop from the extracted Ferrari blocks, in my
 * perspective — NOT by copying the form's pickup/delivery labels.
 *
 *   inbound  → my stop is the DELIVERY to the consignee (the Carolina customer).
 *              The form's "Pickup Information" (often a distant city) is
 *              upstream history, not my stop.
 *   outbound → my stop is the local PICKUP (the Carolina pickup block).
 *              The far-end consignee is downstream, not my stop.
 *
 * Returns a stop object in the same shape the parser emits for stops[]
 * (so it flows through the existing location-dedup pipeline), WITHOUT the
 * airport stop — the modal injects that in the right position. Returns null
 * if the source block is empty (caller should flag for review).
 */
export function buildFerrariLocalStop(extracted = {}, jobType) {
  const ex = extracted || {};
  const block = jobType === "inbound" ? ex.consignee : ex.pickupInfo;
  if (!block || (!block.name && !block.address)) return null;
  return {
    type: jobType === "inbound" ? "delivery" : "pickup",
    locationName: block.name || "",
    address: blockAddress(block),
    contactName: block.contactName || null,
    contactPhone: block.contactPhone || null,
    contactEmail: block.contactEmail || null,
    scheduledTime: null,
  };
}
